import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useGoogleReCaptcha } from "@google-recaptcha/react";
import authApi from "services/authService";
import { useStore } from "context/StoreContext";
import { getLogoUrl } from "utils/logoUtils";

const ForgotPassword = () => {
 const { t } = useTranslation();
 const [email, setEmail] = useState<string>("");
 const [error, setError] = useState("");
 const [success, setSuccess] = useState(false);
 const [loading, setLoading] = useState(false);
 const googleReCaptcha = useGoogleReCaptcha();
 const { store } = useStore();

 const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setError("");
  setSuccess(false);
  setLoading(true);

  try {
   // In test mode (Cypress), bypass reCAPTCHA
   const isTestMode = typeof window !== "undefined" && (window as any).Cypress;
   let captchaValue = "";
   
   if (isTestMode) {
    captchaValue = "test-token-cypress";
   } else {
    captchaValue = await (googleReCaptcha as any)?.executeV3("action");
    if (!captchaValue) {
     setError(t("auth.please_complete_the_captcha"));
     setLoading(false);
     return;
    }
   }

   await authApi.forgotPassword(email, captchaValue);
   setSuccess(true);
  } catch (err: any) {
   setError(
    err?.response?.data?.errors?.[0]?.msg ||
     err?.response?.data?.message ||
     t("auth.an_error_occurred_please_try_again")
   );
  } finally {
   setLoading(false);
  }
 };

 return (
  <div
   className="flex items-center justify-center min-h-screen"
   style={{
    background: "linear-gradient(135deg, #F5F1ED 0%, #EBE5DE 100%)",
   }}
  >
   <div className="w-full max-w-md">
    {/* Header */}
    <div className="text-center mb-8">
      {store?.logo && (
        <div className="mb-4 flex justify-center">
          <img
            src={getLogoUrl(store.logo)}
            alt={store.name || t("common.appName")}
            className="h-16 w-auto object-contain"
          />
        </div>
      )}
      <h1 className="text-4xl font-bold text-store-primary mb-2 font-store">
        {store?.name || t("common.appName")}
      </h1>
      {!store && (
        <p className="text-store-accent text-sm tracking-widest">
          {t("common.appDescription")}
        </p>
      )}
    </div>

    {/* Form Card */}
    <div className="bg-white  overflow-hidden border-store-accent">
     <form onSubmit={handleSubmit} className="p-8 space-y-6">
      <div>
       <h2 className="text-2xl font-bold text-store-primary mb-1">
        {t("auth.reset_password")}
       </h2>
       <p className="text-store-primary-light text-sm">
        {t("auth.enter_your_email_address_and_we_ll_send_you_a_link_to_reset_your_password")}
       </p>
      </div>

      {error && (
       <div className="bg-red-50 border border-red-200 p-4">
        <p className="text-red-700 text-sm font-medium">{error}</p>
       </div>
      )}

      {success && (
       <div className="bg-green-50 border border-green-200 p-4">
        <p className="text-green-700 text-sm font-medium">
         {t("auth.password_reset_link_sent")}
        </p>
       </div>
      )}

      {!success && (
       <>
        <div>
         <label className="block text-sm font-semibold text-store-primary mb-2">
          {t("auth.email")}
         </label>
         <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t("common.placeholders.enterEmail")}
          required
          className="w-full px-4 py-3 border-2 border-store-accent-light focus:outline-none focus:border-store-secondary focus:ring-2 focus:ring-store-secondary focus:ring-opacity-20 transition-all"
         />
        </div>

        <button
         type="submit"
         disabled={loading}
         className="w-full bg-store-primary text-white font-semibold py-3 hover:bg-store-secondary transition-all hover: disabled:opacity-50 disabled:cursor-not-allowed"
        >
         {loading ? t("auth.sending") : t("auth.send_reset_link")}
        </button>
       </>
      )}

      {success && (
       <Link
        to="/login"
        className="block w-full bg-store-primary text-white font-semibold py-3 hover:bg-store-secondary transition-all hover: text-center"
       >
        {t("auth.back_to_login")}
       </Link>
      )}
     </form>

     {/* Divider */}
     <div className="px-8 py-4 bg-store-background border-t border-store-accent-light">
      <p className="text-center text-sm text-store-primary-light">
       {t("auth.remember_your_password")}?{" "}
       <Link
        to="/login"
        className="font-semibold text-store-secondary hover:text-store-secondary-dark transition-colors"
       >
        {t("auth.login_here")}
       </Link>
      </p>
     </div>
    </div>

    {/* Footer Note */}
    <p className="text-center text-xs text-store-primary-light mt-6">
     {t("auth.reCaptchaNote")}
    </p>
   </div>
  </div>
 );
};

export default ForgotPassword;





